import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';
import SampleCall from './SampleCall';
import { findSolution } from './solutionsData';
import {
  T, SURFACE, HAIRLINE, SHADOW, Hero, GhostButton, DemoLink, Band, CenterHead, StatStrip,
} from './siteUI';

/* =========================================================
   Hotel Front Desk — a bespoke solution page.

   The generic /site/solutions/:slug template is too plain for this flagship
   solution, so this page is routed ahead of it (see Router.js) and composed
   from the same siteUI primitives, so it reads as part of the site.
   ========================================================= */

const STAGES = [
  {
    n: '01', title: 'Before the stay',
    desc: 'Answer availability and policy questions, capture direct bookings, send confirmations, and offer approved upgrades.',
    items: ['Reservations & modifications', 'Pre-arrival information', 'Upsells & add-ons'],
  },
  {
    n: '02', title: 'During the stay',
    desc: 'Resolve questions instantly, route service requests, and keep the guest updated without tying up the lobby team.',
    items: ['Housekeeping & maintenance', 'Amenities & local guidance', 'Urgent human escalation'],
  },
  {
    n: '03', title: 'After checkout',
    desc: 'Help with receipts and lost items, collect feedback, and invite guests back with timely follow-up.',
    items: ['Receipts & folio questions', 'Lost-and-found intake', 'Feedback & follow-up'],
  },
];

const ACTIONS = [
  { tag: 'Answer', cls: 'bg-teal-600', desc: 'Approved property knowledge' },
  { tag: 'Complete', cls: 'bg-emerald-600', desc: 'PMS & workflow actions' },
  { tag: 'Route', cls: 'bg-sky-500', desc: 'Housekeeping & maintenance' },
  { tag: 'Escalate', cls: 'bg-orange-500', desc: 'VIP, complaint, or urgent issue' },
];

const POINTS = [
  { n: '01', h: 'No hold-time bottleneck', p: 'Handle busy periods and after-hours requests simultaneously.' },
  { n: '02', h: 'Consistent information', p: 'One property-approved source of truth across every channel.' },
  { n: '03', h: 'Always a human option', p: 'Transfer exceptions and sensitive moments using your rules.' },
];

const BADGES = [
  ['PCI DSS', 'Payment secure'], ['ISO 27001', 'Certified'], ['SOC 2', 'Type II'], ['GDPR', 'Data privacy'],
];

const CHANNELS = ['Voice', 'SMS', 'Web chat', 'Email', 'In-room voice'];

const LiveCallCard = () => (
  <div className={`overflow-hidden rounded-2xl bg-white ${HAIRLINE} ${SHADOW.raised}`}>
    <div className="flex items-center justify-between bg-gradient-to-br from-teal-600 to-teal-700 px-5 py-3.5 text-white">
      <span className="inline-flex items-center gap-2 text-[13px] font-semibold">
        <span className="h-2 w-2 animate-pulse rounded-full bg-teal-200" /> AI front desk · Live
      </span>
      <span className="text-[12.5px] tabular-nums opacity-80">00:18</span>
    </div>
    <div className="p-5">
      <div className="mb-4 flex h-11 items-end gap-1" aria-hidden="true">
        {Array.from({ length: 15 }).map((_, i) => (
          <span
            key={i}
            className="flex-1 animate-pulse rounded-sm bg-gradient-to-t from-teal-700 to-teal-400"
            style={{ height: `${22 + ((i * 9) % 68)}%`, animationDelay: `${(i % 5) * 0.12}s` }}
          />
        ))}
      </div>
      <div className={`rounded-xl bg-slate-50 p-4 ${HAIRLINE}`}>
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-slate-500">
          <span className="grid h-6 w-6 place-items-center rounded-md bg-teal-600 text-[11px] font-bold text-white">FR</span>
          FreshRoad Front Desk · Listening in English
        </div>
        <p className="mt-2 text-[15px] font-medium text-slate-900">
          &ldquo;I found a Deluxe King for your dates. Would you like me to reserve it?&rdquo;
        </p>
      </div>
      <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-emerald-50 px-3.5 py-3 ring-1 ring-emerald-600/15">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-teal-600 text-white"><Check size={13} strokeWidth={3} /></span>
        <div>
          <p className="text-[13px] font-bold text-teal-700">Reservation captured</p>
          <p className="text-[11.5px] text-slate-500">Confirmation sent automatically</p>
        </div>
      </div>
    </div>
  </div>
);

const DemoSiteHotelFrontDesk = () => {
  const sample = findSolution('hotel-front-desk')?.sample;

  return (
    <>
      <Breadcrumb trail={['Home', 'Solutions', 'Hotel Front Desk']} />

      {/* Hero */}
      <section className="grid grid-cols-1 items-center gap-10 py-8 lg:grid-cols-[1.05fr_1fr]">
        <Hero
          eyebrow="FreshRoad Connect for Hotels"
          title="Your front desk never misses a guest."
          deck="Answer every call, message, and request — around the clock."
          actions={
            <>
              <DemoLink>Book a hotel demo</DemoLink>
              <GhostButton className="!text-teal-700">
                <Link to="/site/conversational-ai" className="inline-flex items-center gap-2">
                  How it runs <ArrowRight size={15} />
                </Link>
              </GhostButton>
            </>
          }
          footerLead={sample && <SampleCall src={sample.src} label={sample.label} caption={sample.caption} />}
        >
          FreshRoad handles routine guest service, completes hotel workflows, and brings in your team when the human touch matters.
        </Hero>
        <LiveCallCard />
      </section>

      {/* Stats */}
      <section className="py-8">
        <StatStrip
          stats={[
            { value: '24/7', label: 'Guest service' },
            { value: '200', label: 'Simultaneous calls' },
            { value: '∞', label: 'Languages & channels' },
            { value: '0s', label: 'Hold time' },
          ]}
        />
      </section>

      {/* Before / during / after */}
      <section className="px-2 py-8">
        <CenterHead
          title="Before, during, and after every stay"
          subtitle="One consistent guest experience — while your team gets fewer interruptions and better information."
        />
        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {STAGES.map(({ n, title, desc, items }) => (
            <div key={n} className={`flex flex-col p-6 ${SURFACE.cardHover}`}>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 text-[14px] font-bold text-teal-700">{n}</span>
              <p className="mt-4 text-[15px] font-semibold">{title}</p>
              <p className={`mt-1.5 ${T.body}`}>{desc}</p>
              <ul className="mt-4 flex flex-col gap-2 border-t border-slate-200/70 pt-4">
                {items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13.5px] text-slate-700">
                    <Check size={15} strokeWidth={3} className="mt-0.5 shrink-0 text-teal-600" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* From conversation to completed work */}
      <Band title="Not another chatbot. A working front desk.">
        <p className={`mx-auto mt-3 max-w-[60ch] text-center ${T.lede}`}>
          FreshRoad understands the request, retrieves property-approved information, takes the permitted action, and records the outcome.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {CHANNELS.map((c, i) => (
            <span
              key={c}
              className={`rounded-xl px-3.5 py-2 text-[13px] font-semibold ${
                i === 0 ? 'bg-teal-600 text-white' : `bg-white text-slate-600 ${HAIRLINE}`
              }`}
            >
              {c}
            </span>
          ))}
        </div>
        <div className="mx-auto mt-8 grid max-w-[880px] grid-cols-1 items-center gap-4 lg:grid-cols-[1fr_auto_1.3fr]">
          <div className={`rounded-2xl p-5 ${HAIRLINE} bg-white`}>
            <p className="text-[11.5px] font-bold uppercase tracking-wider text-slate-400">Guest asks</p>
            <p className="mt-1.5 text-[16px] font-bold text-slate-900">&ldquo;Can I check out late?&rdquo;</p>
            <div className="mt-4 rounded-xl bg-gradient-to-br from-teal-600 to-teal-700 p-4 text-white">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/15"><Sparkles size={16} /></span>
              <p className="mt-2 text-[11.5px] font-bold uppercase tracking-wider text-teal-100">FreshRoad AI</p>
              <p className="text-[15px] font-bold">Understands · checks · acts</p>
            </div>
          </div>
          <div className="hidden justify-self-center text-slate-300 lg:block">
            <ArrowRight size={26} />
          </div>
          <div className="flex flex-col gap-2.5">
            {ACTIONS.map(({ tag, cls, desc }) => (
              <div key={tag} className={`flex items-center gap-3 rounded-xl p-3.5 ${HAIRLINE} bg-white`}>
                <span className={`rounded-md px-2 py-0.5 text-[12px] font-bold text-white ${cls}`}>{tag}</span>
                <span className="text-[13px] text-slate-600">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </Band>

      {/* Give your team back the lobby */}
      <section className="px-2 py-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="rounded-[28px] bg-gradient-to-br from-teal-600 to-teal-700 p-9 text-white">
            <p className="text-[11.5px] font-bold uppercase tracking-wider text-teal-100">Illustrative annual impact</p>
            <p className="mt-3 text-[46px] font-bold tracking-[-0.02em]">$50K–$100K</p>
            <p className="mt-1 text-[14px] text-teal-50/90">
              Potential savings per property where routine calls, messages, and requests consume the equivalent of two to five full-time roles.
            </p>
            <p className="mt-5 text-[11.5px] text-teal-100/70">
              Actual results vary by property, volume, staffing model, integrations, and local labor costs.
            </p>
          </div>
          <div>
            <CenterHead
              title="Give your team back the lobby."
              subtitle="AI handles the repetitive communication load. Your staff spends more time welcoming guests and creating memorable stays."
            />
            <div className="mt-7 flex flex-col gap-5">
              {POINTS.map(({ n, h, p }) => (
                <div key={n} className="flex gap-4">
                  <span className="text-[15px] font-bold tabular-nums text-teal-600">{n}</span>
                  <div>
                    <p className="text-[15px] font-semibold">{h}</p>
                    <p className={`mt-0.5 ${T.body}`}>{p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <Band title="Built for responsible guest service.">
        <p className={`mx-auto mt-3 max-w-[56ch] text-center ${T.lede}`}>
          Guest and payment data stays protected, access is controlled, and every action is logged for your team to review.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3.5">
          {BADGES.map(([name, sub]) => (
            <div key={name} className={`flex items-center gap-3 rounded-2xl bg-white px-5 py-4 ${HAIRLINE}`}>
              <span className="grid h-7 w-7 place-items-center rounded-full bg-teal-50 text-teal-700">
                <Check size={14} strokeWidth={3} />
              </span>
              <div>
                <p className="text-[14px] font-bold">{name}</p>
                <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </Band>

      <CtaBand
        title="See the AI front desk on your own calls."
        subtitle="Book a demo and we'll run it against a real guest workflow — in your voice, your languages, your rules."
        primary="Book a hotel demo"
      />
    </>
  );
};

export default DemoSiteHotelFrontDesk;
